/**
 * Helper for reading every record from a paginated list endpoint.
 *
 * Several list endpoints cap how much they return per request (the job
 * applications endpoint defaults to 50 and caps at 100). A caller that ignores
 * the pagination metadata silently sees only the first page, which looks like
 * missing data rather than an error. This walks the pages and returns the
 * whole set.
 */

// The applications endpoint caps `limit` at 100; requesting the maximum keeps
// the number of round trips down.
const DEFAULT_PAGE_SIZE = 100;

// Stops a malformed or unexpected response from looping indefinitely.
const MAX_PAGES = 200;

/**
 * Work out how many pages an endpoint has from its first response. The list
 * endpoints in this codebase use three shapes:
 *   - pagination.totalPages      (job applications)
 *   - pagination.pages           (assessments, interviewer assessments)
 *   - top-level total + limit    (my-interviews)
 * Returns 0 when no usable metadata is present.
 */
function resolveTotalPages(data) {
  const pagination = data?.pagination;
  if (pagination?.totalPages) return pagination.totalPages;
  if (pagination?.pages) return pagination.pages;

  const total = pagination?.total ?? data?.total;
  const limit = pagination?.limit ?? data?.limit;
  if (total && limit) return Math.ceil(total / limit);

  return 0;
}

/**
 * Fetch every page of a paginated endpoint and return the combined records.
 *
 * @param {Function} fetchPage - Called as fetchPage({ page, limit }); must
 *   resolve to the axios response for that page.
 * @param {Function} selectItems - Pulls the records array out of response.data.
 * @param {object} [options]
 * @param {number} [options.pageSize] - Records per request.
 * @returns {Promise<Array>} Every record across all pages.
 */
export async function fetchAllPages(fetchPage, selectItems, options = {}) {
  const limit = options.pageSize || DEFAULT_PAGE_SIZE;

  const firstResponse = await fetchPage({ page: 1, limit });
  const firstItems = selectItems(firstResponse.data) || [];

  const totalPages = resolveTotalPages(firstResponse.data);

  // No pagination metadata, or everything already fits in one page.
  if (!totalPages || totalPages <= 1) {
    return firstItems;
  }

  const pageCount = Math.min(totalPages, MAX_PAGES);
  const remaining = [];
  for (let page = 2; page <= pageCount; page++) {
    remaining.push(fetchPage({ page, limit }));
  }

  const responses = await Promise.all(remaining);
  return responses.reduce(
    (all, response) => all.concat(selectItems(response.data) || []),
    firstItems
  );
}

export default fetchAllPages;
